/**
 * Onboarding Internationalization (i18n) Strings
 *
 * All user-facing text for the onboarding system.
 * Add new languages by adding new locale keys.
 */

const onboardingStrings = {
  en: {
    // Common
    common: {
      next: 'Next',
      back: 'Back',
      skip: 'Skip',
      getStarted: 'Get Started',
      saving: 'Saving...',
      close: 'Close',
      tryIt: 'Try it now',
      learnMore: 'Learn more',
      gotIt: 'Got it',
      dontShowAgain: "Don't show again",
      resumeTour: 'Resume tour',
      pauseTitle: 'Walkthrough paused',
      pauseDescription: 'Try the feature in the app, then resume the walkthrough.',
      stepOf: 'Step {current} of {total}',
    },

    // Welcome step
    welcome: {
      title: 'Welcome to Uni-Fund!',
      description: "Hi {name}! Let's take a quick tour of the platform to help you get started.",
      content: 'Uni-Fund is your comprehensive platform for university financial planning, budgeting, and resource management.',
    },

    // Dashboard step
    dashboard: {
      title: 'Your Dashboard',
      description: 'Your personalized command center for tasks, notifications, and quick access to key metrics.',
      action: 'Go to Dashboard',
      features: {
        tasks: {
          title: 'Pending Tasks',
          description: 'See approvals, reviews, and items needing your attention',
        },
        metrics: {
          title: 'Key Metrics',
          description: 'Quick view of budget status, spending, and trends',
        },
      },
    },

    // Global Search step
    search: {
      title: 'Global Search',
      description: 'Quickly find anything with the command palette. Press {shortcut} anytime!',
      content: 'Search for pages, actions, and more from anywhere in the app',
      shortcutMac: 'Cmd+K',
      shortcutWin: 'Ctrl+K',
    },

    // Quick Actions step
    quickActions: {
      title: 'Quick Actions',
      description: 'The floating action button gives you instant access to common tasks based on your role.',
      content: 'Look for this button in the bottom-right corner for quick shortcuts',
    },

    // Admin step
    admin: {
      title: 'Admin Panel',
      description: 'As an admin, you have access to system settings, user management, and configuration.',
      action: 'Open Admin Panel',
      features: {
        users: 'User accounts and roles',
        workflow: 'Workflow configuration',
        settings: 'System settings and branding',
      },
    },

    // Budget Manager steps
    budgetManager: {
      transfers: {
        title: 'Budget Transfers',
        description: 'Easily transfer funds between cost centers and track all movements.',
        action: 'View Transfers',
      },
      scenarios: {
        title: 'Budget Scenarios',
        description: 'Create what-if scenarios to model different budget allocations.',
        action: 'Explore Scenarios',
      },
    },

    // Researcher steps
    researcher: {
      portfolio: {
        title: 'Research Portfolio',
        description: 'Track your grants, commitments, and research funding in one place.',
        action: 'View Portfolio',
      },
      commitments: {
        title: 'Research Commitments',
        description: 'Manage cost-sharing and commitment obligations across your projects.',
        action: 'Manage Commitments',
      },
    },

    // HR steps
    hr: {
      positions: {
        title: 'Position Transfers',
        description: 'Move funds between cost centers to support staffing changes and open roles.',
        action: 'Open Transfers',
      },
      whatIf: {
        title: 'Personnel What-If',
        description: 'Model staffing scenarios and see the financial impact of changes.',
        action: 'Try What-If',
      },
    },

    // Finance steps
    finance: {
      revenue: {
        title: 'Revenue Recognition',
        description: 'Manage deferred revenue schedules and recognition timing.',
        action: 'View Revenue',
      },
      margins: {
        title: 'Program Margins',
        description: 'Analyze profitability across programs and identify opportunities.',
        action: 'View Margins',
      },
    },

    // Campus & Department Management
    campusSummary: {
      title: 'Campus Summary',
      description: 'Get a bird\'s-eye view of financial health across all departments and units.',
      action: 'View Campus Summary',
      features: {
        overview: 'Institution-wide financial metrics',
        comparison: 'Cross-department comparisons',
        trends: 'Historical trends and projections',
      },
    },
    departmentOverview: {
      title: 'Department Overview',
      description: 'Drill down into individual department budgets, spending, and performance.',
      action: 'Explore Departments',
    },
    budgetModel: {
      title: 'Budget Model',
      description: 'Configure and manage your institution\'s budget allocation methodology.',
      action: 'Open Budget Model',
      features: {
        allocation: 'Allocation rules and formulas',
        drivers: 'Cost drivers and metrics',
        scenarios: 'Model different allocation approaches',
      },
    },
    budgetVsActual: {
      title: 'Budget vs Actual',
      description: 'Compare planned budgets against actual spending with variance analysis.',
      action: 'View Report',
    },

    // Capital Planning
    capitalProjects: {
      title: 'Capital Projects',
      description: 'Track major construction, renovation, and facility projects.',
      action: 'View Projects',
      features: {
        timeline: 'Project timelines and milestones',
        funding: 'Funding sources and draws',
        status: 'Current status and progress',
      },
    },
    capitalPlanning: {
      title: 'Capital Planning',
      description: 'Plan multi-year capital investments and prioritize projects.',
      action: 'Open Planning',
    },
    capitalPortfolio: {
      title: 'Capital Portfolio',
      description: 'Dashboard view of your entire capital project portfolio.',
      action: 'View Portfolio',
    },

    // Tuition & Enrollment
    feeProjection: {
      title: 'Fee Projection',
      description: 'Model tuition and fee scenarios based on enrollment forecasts.',
      action: 'Open Projections',
      features: {
        modeling: 'Rate increase modeling',
        revenue: 'Revenue impact analysis',
        comparison: 'Compare fee scenarios',
      },
    },
    enrollmentFunnel: {
      title: 'Enrollment Funnel',
      description: 'Track admissions pipeline and forecast enrollment numbers.',
      action: 'View Funnel',
    },

    // Extended Research
    researchExplorer: {
      title: 'Research Explorer',
      description: 'Search and analyze your institution\'s research portfolio.',
      action: 'Open Explorer',
    },
    researchProposals: {
      title: 'Research Proposals',
      description: 'Track proposal submissions, success rates, and pipeline.',
      action: 'View Proposals',
    },
    researchSubawards: {
      title: 'Subaward Management',
      description: 'Manage subaward agreements and monitor subrecipient spending.',
      action: 'View Subawards',
    },
    researchBudgetBuilder: {
      title: 'Research Budget Builder',
      description: 'Build compliant proposal budgets with automated calculations.',
      action: 'Build Budget',
      features: {
        templates: 'Sponsor-specific templates',
        rates: 'Current F&A and fringe rates',
        validation: 'Compliance validation',
      },
    },
    researchCloseout: {
      title: 'Closeout Readiness',
      description: 'Track grants approaching end dates and prepare for closeout.',
      action: 'View Closeout',
    },
    researchCompliance: {
      title: 'Compliance & Risk',
      description: 'Monitor compliance issues and research risk indicators.',
      action: 'View Compliance',
    },

    // Analytics & Reporting
    analyticsBi: {
      title: 'Analytics & BI',
      description: 'Access embedded analytics dashboards and custom reports.',
      action: 'Open Analytics',
      features: {
        dashboards: 'Pre-built dashboards',
        custom: 'Custom report builder',
        export: 'Export to multiple formats',
      },
    },
    budgetBook: {
      title: 'Budget Book',
      description: 'Generate formal budget book reports for board presentations.',
      action: 'View Budget Book',
    },
    deficitCenters: {
      title: 'Deficit Cost Centers',
      description: 'Identify and monitor cost centers with deficit balances.',
      action: 'View Deficits',
    },

    // Workflow & Approvals
    approvalQueue: {
      title: 'Approval Queue',
      description: 'Review and process pending approval requests in one place.',
      action: 'Open Queue',
      features: {
        batch: 'Batch approve/reject',
        delegate: 'Delegate approvals',
        history: 'Approval history',
      },
    },
    workflowBuilder: {
      title: 'Workflow Builder',
      description: 'Design custom approval workflows for your organization.',
      action: 'Build Workflow',
      features: {
        visual: 'Visual workflow designer',
        conditions: 'Conditional routing',
        notifications: 'Automated notifications',
      },
    },

    // Help & Settings
    helpCenter: {
      title: 'Help Center',
      description: 'Access documentation, tutorials, and support resources.',
      action: 'Open Help',
    },
    accountSettings: {
      title: 'Account Settings',
      description: 'Customize your profile, preferences, and notifications.',
      action: 'Open Settings',
    },

    // Forms & Dynamic Submissions
    formBuilder: {
      title: 'Form Builder',
      description: 'Create dynamic forms with drag-and-drop field configuration.',
      action: 'Build Forms',
      features: {
        fields: 'Drag-and-drop field types',
        logic: 'Conditional logic and validation',
        workflow: 'Submission workflow integration',
      },
    },
    formSubmissions: {
      title: 'Form Submissions',
      description: 'Review, process, and export form submission data.',
      action: 'View Submissions',
    },
    publicForms: {
      title: 'Public Forms',
      description: 'Share forms externally with customizable public links.',
      action: 'Configure Public Access',
    },

    // Research Commitments
    researchCommitments: {
      title: 'Research Commitments',
      description: 'Track cost-sharing and matching obligations for research grants.',
      action: 'View Commitments',
    },

    // Finance features
    revenueRecognition: {
      title: 'Revenue Recognition',
      description: 'Manage deferred revenue schedules and ASC 606 compliance.',
      action: 'View Revenue',
    },
    programMargins: {
      title: 'Program Margins',
      description: 'Analyze program profitability and cost allocation.',
      action: 'View Margins',
    },
    personnelWhatIf: {
      title: 'Personnel What-If',
      description: 'Model staffing scenarios and financial impact.',
      action: 'Create Scenario',
    },

    // Complete step
    complete: {
      title: "You're All Set!",
      description: 'You can revisit this guide anytime from your account settings.',
      content: "Ready to explore! If you need help, look for the help icon or check the documentation.",
    },

    // Getting Started Checklist
    checklist: {
      title: 'Getting Started',
      subtitle: 'Complete these steps to get the most out of Uni-Fund',
      collapse: 'Collapse',
      expand: 'Expand',
      progress: '{completed} of {total} complete',
      items: {
        completeProfile: {
          title: 'Complete your profile',
          description: 'Add your name and preferences',
        },
        exploreDashboard: {
          title: 'Explore the dashboard',
          description: 'View your tasks and notifications',
        },
        trySearch: {
          title: 'Try the global search',
          description: 'Press {shortcut} to search anything',
        },
        viewBudget: {
          title: 'View a department budget',
          description: 'Navigate to a cost center',
        },
        setNotifications: {
          title: 'Set notification preferences',
          description: 'Customize how you receive alerts',
        },
      },
    },

    // Contextual Tips
    tips: {
      globalSearch: {
        title: 'Pro tip: Global Search',
        content: 'Press {shortcut} anywhere to quickly search and navigate',
      },
      quickActions: {
        title: 'Quick Actions Available',
        content: 'Click the action button for role-specific shortcuts',
      },
      keyboardNav: {
        title: 'Keyboard Navigation',
        content: 'Use arrow keys to navigate, Enter to select, Escape to close',
      },
      savedFilters: {
        title: 'Save Your Filters',
        content: 'Click the star icon to save frequently used filter combinations',
      },
      darkMode: {
        title: 'Dark Mode Available',
        content: 'Toggle dark mode in Account Settings for a different look',
      },
    },

    // Errors
    errors: {
      saveFailed: 'Failed to save. Click "Get Started" to retry, or skip to continue.',
      loadFailed: 'Failed to load onboarding progress',
    },
  },

  // Spanish translations (example for i18n support)
  es: {
    common: {
      next: 'Siguiente',
      back: 'Atr\u00e1s',
      skip: 'Omitir',
      getStarted: 'Comenzar',
      saving: 'Guardando...',
      close: 'Cerrar',
      tryIt: 'Probar ahora',
      learnMore: 'Saber m\u00e1s',
      gotIt: 'Entendido',
      dontShowAgain: 'No mostrar de nuevo',
      resumeTour: 'Reanudar recorrido',
      pauseTitle: 'Recorrido en pausa',
      pauseDescription: 'Prueba la funcion en la app y luego reanuda el recorrido.',
      stepOf: 'Paso {current} de {total}',
    },
    welcome: {
      title: '\u00a1Bienvenido a Uni-Fund!',
      description: '\u00a1Hola {name}! Hagamos un recorrido r\u00e1pido por la plataforma.',
      content: 'Uni-Fund es tu plataforma integral para la planificaci\u00f3n financiera universitaria.',
    },
    // Add more Spanish translations as needed...
  },
};

/**
 * Get localized string with interpolation
 * @param {string} locale - Locale code (e.g., 'en', 'es')
 * @param {string} path - Dot-notation path to string (e.g., 'welcome.title')
 * @param {Object} vars - Variables to interpolate (e.g., { name: 'John' })
 * @returns {string} Localized string
 */
export function t(locale, path, vars = {}) {
  const keys = path.split('.');
  let value = onboardingStrings[locale] || onboardingStrings.en;

  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      // Fallback to English
      value = onboardingStrings.en;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
      break;
    }
  }

  if (typeof value !== 'string') {
    return path; // Return path if not found
  }

  // Interpolate variables
  return value.replace(/\{(\w+)\}/g, (match, key) => {
    return vars[key] !== undefined ? vars[key] : match;
  });
}

/**
 * Get all strings for a locale
 * @param {string} locale - Locale code
 * @returns {Object} All strings for the locale
 */
export function getStrings(locale) {
  return onboardingStrings[locale] || onboardingStrings.en;
}

/**
 * Available locales
 */
export const AVAILABLE_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Espa\u00f1ol' },
];

export default onboardingStrings;
